import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateTaskCN, deleteTaskCN, setSelectedTask } from 'slices/taskCNSlice'
import { updatePortfolioTask, deletePortfolioTask } from 'slices/portfolioTaskCNSlice'
import axios from 'axios'
import { Sheet } from 'components/ui/sheet'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Button } from 'components/ui/button'
import { Check, Trash2, Clock, FolderOpen, ListTodo } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { toast } from 'components/ui/toast-store'
import { canCompleteTask } from 'utils/taskPermissions'

const PRIORITY_OPTIONS = [
	{ value: 'P1 - Urgent', label: 'P1 - Urgent', color: 'text-red-500' },
	{ value: 'P2 - High', label: 'P2 - High', color: 'text-orange-500' },
	{ value: 'P3 - Medium', label: 'P3 - Medium', color: 'text-blue-500' },
	{ value: 'P4 - Low', label: 'P4 - Low', color: 'text-gray-500' },
]

const EFFORT_OPTIONS = [
	{ value: 'XS - Trivial', label: 'XS - Trivial' },
	{ value: 'S - Small', label: 'S - Small' },
	{ value: 'M - Medium', label: 'M - Medium' },
	{ value: 'L - Large', label: 'L - Large' },
	{ value: 'XL - Epic', label: 'XL - Epic' },
]

// Activity action config - keyed by backend action strings
const ACTIVITY_ACTION_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
	'Task Created': {
		color: '#3b82f6',
		icon: (
			<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
			</svg>
		),
		label: 'Task Created',
	},
	'Field Updated': {
		color: '#f59e0b',
		icon: (
			<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
			</svg>
		),
		label: 'Updated',
	},
	'Status Changed': {
		color: '#f59e0b',
		icon: (
			<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
			</svg>
		),
		label: 'Status Changed',
	},
	'Task Completed': {
		color: '#10b981',
		icon: (
			<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
			</svg>
		),
		label: 'Task Completed',
	},
	'Task Reopened': {
		color: '#8b5cf6',
		icon: (
			<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
			</svg>
		),
		label: 'Task Reopened',
	},
}

// Fallback config for unknown action types
const DEFAULT_ACTION_CONFIG = ACTIVITY_ACTION_CONFIG['Field Updated']

// Humanize field names for activity display
function formatFieldName(field: string): string {
	const fieldMap: Record<string, string> = {
		taskName: 'Name',
		taskPriority: 'Priority',
		taskEffort: 'Effort',
		taskStatus: 'Status',
		dateDue: 'Due Date',
		dateCompleted: 'Completed Date',
		description: 'Description',
		completed: 'Completed',
		projectid: 'Project',
		assignedTo: 'Assigned To',
	}
	return fieldMap[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
}

// Quick date helpers
function getToday(): string {
	const d = new Date()
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getTomorrow(): string {
	const d = new Date()
	d.setDate(d.getDate() + 1)
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getNextWeek(): string {
	const d = new Date()
	d.setDate(d.getDate() + 7)
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDate(dateStr: string | null): string {
	if (!dateStr) return 'No date'
	const d = new Date(dateStr)
	// Use UTC methods to avoid timezone shift on ISO date strings
	return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
		.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Convert a YYYY-MM-DD string to an ISO string at noon UTC to prevent day-boundary timezone drift
function toNoonUTC(dateStr: string): string {
	return `${dateStr}T12:00:00.000Z`
}

// Combine a YYYY-MM-DD date with an HH:MM local time into an ISO string. Used when
// a task has hasScheduledTime=true so the chosen wall-clock time is preserved.
function toIsoWithLocalTime(dateStr: string, timeHM: string): string {
	return new Date(`${dateStr}T${timeHM}:00`).toISOString()
}

// Extract a HH:MM (24h) string from an existing ISO/Date for the <input type="time"> value.
function extractTimeHM(dateStr: string | Date | null | undefined): string {
	if (!dateStr) return '09:00'
	const d = new Date(dateStr)
	const hh = String(d.getHours()).padStart(2, '0')
	const mm = String(d.getMinutes()).padStart(2, '0')
	return `${hh}:${mm}`
}

// Format project display label: "Project Name - Asset Name"
function formatProjectLabel(project: any): string {
	if (!project) return ''
	const assetName = project.projectAsset?.assetName
	return assetName ? `${project.projectName} - ${assetName}` : project.projectName
}

function formatActivityDate(dateStr: string): string {
	const d = new Date(dateStr)
	const now = new Date()
	const diffMs = now.getTime() - d.getTime()
	const diffMins = Math.floor(diffMs / 60000)
	const diffHrs = Math.floor(diffMs / 3600000)
	const diffDays = Math.floor(diffMs / 86400000)

	if (diffMins < 1) return 'Just now'
	if (diffMins < 60) return `${diffMins}m ago`
	if (diffHrs < 24) return `${diffHrs}h ago`
	if (diffDays < 7) return `${diffDays}d ago`
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface TaskCNDetailSheetProps {
	task: any
	open: boolean
	onClose: () => void
	/**
	 * Which kind of task this is. Defaults to 'taskcn' (the legacy behavior used by
	 * Project/Location task screens). When 'portfoliotask', updates and deletes route
	 * to the portfolioTaskCN slice and the project picker is hidden.
	 */
	taskKind?: 'taskcn' | 'portfoliotask'
	/** Required when `taskKind === 'portfoliotask'` — used to scope the slice action. */
	portfolioId?: string
}

const TaskCNDetailSheet = ({
	task,
	open,
	onClose,
	taskKind = 'taskcn',
	portfolioId,
}: TaskCNDetailSheetProps) => {
	const dispatch = useDispatch()
	const isPortfolioTask = taskKind === 'portfoliotask'

	const [editingTitle, setEditingTitle] = useState<string | null>(null)
	const [editingDescription, setEditingDescription] = useState<string | null>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const dateInputRef = useRef<HTMLInputElement>(null)

	// Fetch projects directly (bypasses shared project slice which can be overwritten by other screens).
	// Skipped for portfolio tasks — they have no project association.
	const { userInfo } = useSelector((state: any) => state.userLogin)
	const portfolio = useSelector((state: any) =>
		isPortfolioTask && portfolioId ? state.portfolios?.entities?.[portfolioId] : null,
	)
	const [activeProjects, setActiveProjects] = useState<any[]>([])
	useEffect(() => {
		if (isPortfolioTask) return
		if (userInfo?.token) {
			axios
				.get('/api/projects/myProjects', { headers: { Authorization: `Bearer ${userInfo.token}` } })
				.then(res => {
					const projects = (res.data || []).filter((p: any) => p.isActive !== false)
					setActiveProjects(projects)
				})
				.catch(() => {})
		}
	}, [userInfo, isPortfolioTask])

	// Predecessor candidates -- the user's assigned tasks, narrowed by the current
	// task's project. Excludes the task itself to prevent self-references.
	const [candidatePredecessors, setCandidatePredecessors] = useState<any[]>([])
	useEffect(() => {
		if (isPortfolioTask) return
		if (userInfo?.token && userInfo?._id) {
			axios
				.get('/api/taskcns/myassigned', {
					params: { assignedTo: userInfo._id },
					headers: { Authorization: `Bearer ${userInfo.token}` },
				})
				.then(res => setCandidatePredecessors(res.data || []))
				.catch(() => setCandidatePredecessors([]))
		}
	}, [userInfo, isPortfolioTask])

	const predecessorOptions = useMemo(() => {
		if (!task) return []
		const taskProjectId = typeof task.projectid === 'object' ? task.projectid?._id : task.projectid
		return candidatePredecessors.filter((t: any) => {
			if (t._id === task._id) return false
			const projId = typeof t.projectid === 'object' ? t.projectid?._id : t.projectid
			return !taskProjectId || projId === taskProjectId
		})
	}, [candidatePredecessors, task])

	// Build assignable users:
	//   - taskcn      → from the task's project owner + partners + current user
	//   - portfoliotask → from the portfolio's owner + partners + current user
	const assignableUsers = useMemo(() => {
		const users: { id: string; name: string }[] = []
		const push = (id?: string, name?: string) => {
			if (!id || users.some(u => u.id === id)) return
			users.push({ id, name: name || 'Unknown' })
		}

		if (userInfo) push(userInfo._id, userInfo.name)

		if (isPortfolioTask && portfolio) {
			const owner = portfolio.owner
			const ownerId = typeof owner === 'object' ? owner?._id : owner
			const ownerName = typeof owner === 'object' ? owner?.name : undefined
			push(ownerId, ownerName)
			;(portfolio.partners || []).forEach((p: any) => {
				const u = p?.partnerUserId
				push(typeof u === 'object' ? u?._id : u, typeof u === 'object' ? u?.name : undefined)
			})
		} else if (!isPortfolioTask) {
			const taskProjectId = typeof task?.projectid === 'object' ? task?.projectid?._id : task?.projectid
			const project = activeProjects.find((p: any) => p._id === taskProjectId)
			if (project) {
				if (project.owner && project.owner._id !== userInfo?._id) {
					push(project.owner._id, project.owner.name)
				}
				if (Array.isArray(project.partners)) {
					project.partners.forEach((p: any) => {
						push(p?.partnerUserId?._id, p?.partnerUserId?.name)
					})
				}
			}
		}

		// Always ensure current assignee is in the list (in case they were removed from the parent)
		const currentAssigneeId = typeof task?.assignedTo === 'object' ? task?.assignedTo?._id : task?.assignedTo
		const currentAssigneeName = typeof task?.assignedTo === 'object' ? task?.assignedTo?.name : null
		push(currentAssigneeId, currentAssigneeName || undefined)

		return users
	}, [task, activeProjects, userInfo, isPortfolioTask, portfolio])

	// Keyboard shortcuts - must be above early return to preserve hook order
	useEffect(() => {
		if (!task) return
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement
			const isEditing =
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target instanceof HTMLSelectElement

			if (isEditing) {
				if (e.key === 'Escape') target.blur()
				return
			}

			switch (e.key) {
				case 'Escape':
					onClose()
					break
				case 'e':
					e.preventDefault()
					if (!task.completed) setEditingTitle(task.taskName)
					break
				case 'c':
					e.preventDefault()
					// The shortcut takes the same route as the checkbox: the completion
					// right is checked, then confirmed, before anything is dispatched.
					if (!task.completed && canCompleteTask(task, userInfo?._id, [portfolio?.owner])) {
						toast
							.confirm({
								title: 'Complete Task',
								message: `Mark "${task.taskName}" as completed?`,
								confirmLabel: 'Set as Completed',
								cancelLabel: 'Cancel',
								variant: 'default',
							})
							.then(confirmed => {
								if (!confirmed) return
								const completePatch = { completed: true, dateCompleted: new Date().toISOString() }
								if (isPortfolioTask) {
									if (portfolioId) {
										dispatch(updatePortfolioTask({ taskId: task._id, portfolioId, patch: completePatch }) as any)
									}
								} else {
									dispatch(updateTaskCN({ _id: task._id, ...completePatch }) as any)
								}
							})
					}
					break
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [task, onClose, dispatch, isPortfolioTask, portfolioId, userInfo, portfolio])

	if (!task) return <Sheet open={false} onClose={onClose} side="right" className="!w-[500px]"><div /></Sheet>

	const isCompleted = task.completed

	const handleFieldUpdate = (updates: Record<string, any>) => {
		if (isCompleted) return
		if (isPortfolioTask) {
			if (!portfolioId) return
			dispatch(updatePortfolioTask({ taskId: task._id, portfolioId, patch: updates }) as any)
		} else {
			dispatch(updateTaskCN({ _id: task._id, ...updates }) as any)
		}
	}

	const handleTitleBlur = () => {
		if (editingTitle !== null && editingTitle.trim() && editingTitle !== task.taskName) {
			handleFieldUpdate({ taskName: editingTitle.trim() })
		}
		setEditingTitle(null)
	}

	const handleDescriptionBlur = () => {
		if (editingDescription !== null && editingDescription !== (task.description || '')) {
			handleFieldUpdate({ description: editingDescription })
		}
		setEditingDescription(null)
	}

	// Completing a task is limited to its creator, its assignee and the owner of
	// the project, location or portfolio it belongs to. See utils/taskPermissions.
	const mayComplete = canCompleteTask(task, userInfo?._id, [portfolio?.owner])

	const handleToggleComplete = async () => {
		if (isCompleted || !mayComplete) return
		const confirmed = await toast.confirm({
			title: 'Complete Task',
			message: `Mark "${task.taskName}" as completed?`,
			confirmLabel: 'Set as Completed',
			cancelLabel: 'Cancel',
			variant: 'default',
		})
		if (!confirmed) return
		const now = new Date().toISOString()
		handleFieldUpdate({ completed: true, dateCompleted: now })
	}

	const handleDelete = async () => {
		if (isPortfolioTask) {
			if (!portfolioId) return
			await dispatch(deletePortfolioTask({ taskId: task._id, portfolioId }) as any)
		} else {
			await dispatch(deleteTaskCN(task._id) as any)
			dispatch(setSelectedTask(null))
		}
		onClose()
	}

	const dueDateStr = task.dateDue
		? (() => { const d = new Date(task.dateDue); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` })()
		: ''
	const todayStr = getToday()
	const tomorrowStr = getTomorrow()
	const nextWeekStr = getNextWeek()
	const isDueToday = dueDateStr === todayStr
	const isDueTomorrow = dueDateStr === tomorrowStr

	const quickDates: { label: string; value: string }[] = []
	if (!isDueToday) quickDates.push({ label: 'Today', value: todayStr })
	if (!isDueTomorrow) quickDates.push({ label: 'Tomorrow', value: tomorrowStr })
	quickDates.push({ label: 'Next week', value: nextWeekStr })

	return (
		<Sheet open={open} onClose={onClose} side="right" className="!w-[500px]">
			{/* Header */}
			<div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<ListTodo className="w-5 h-5" />
				</div>
				<div className="flex-1 min-w-0">
					<h2 className="text-base font-semibold text-gray-900">Task Details</h2>
					<p className="text-sm text-gray-500">View and edit task information</p>
				</div>
				<div className="flex items-center gap-2 shrink-0">
					<button
						onClick={handleToggleComplete}
						disabled={isCompleted || !mayComplete}
						className={`w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center ${
							isCompleted
								? 'bg-[#0080fe] border-[#0080fe] text-white cursor-not-allowed'
								: mayComplete
									? 'bg-transparent border-gray-300 text-transparent cursor-pointer'
									: 'bg-transparent border-gray-300 text-transparent opacity-40 cursor-not-allowed'
						}`}
						title={
							isCompleted
								? 'Completed'
								: mayComplete
									? 'Mark complete'
									: 'Only the task creator, the assignee or the owner can complete this task'
						}
					>
						{isCompleted && <Check className="w-3 h-3" />}
					</button>
					<span className={`text-sm font-medium ${isCompleted ? 'text-[#0080fe]' : 'text-gray-500'}`}>
						{isCompleted ? 'Done' : 'Active'}
					</span>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-y-auto p-5 space-y-5">
				{/* Title */}
				<div>
					{editingTitle !== null ? (
						<input
							type="text"
							value={editingTitle}
							onChange={e => setEditingTitle(e.target.value)}
							onBlur={handleTitleBlur}
							onKeyDown={e => {
								if (e.key === 'Enter') handleTitleBlur()
								if (e.key === 'Escape') setEditingTitle(null)
							}}
							autoFocus
							className="w-full text-xl font-semibold bg-transparent border-b-2 border-[#0080fe] focus:outline-none pb-1 text-gray-900"
						/>
					) : (
						<h2
							onClick={() => !isCompleted && setEditingTitle(task.taskName)}
							className={`text-xl font-semibold ${
								isCompleted ? 'line-through text-gray-400 cursor-default' : 'text-gray-900 cursor-pointer transition-colors hover:text-[#0080fe]'
							}`}
						>
							{task.taskName}
						</h2>
					)}
				</div>

				{/* Description */}
				<div>
					<Label className="block text-xs font-medium mb-2 text-gray-500">Description</Label>
					{editingDescription !== null ? (
						<div className="space-y-2">
							<Textarea
								value={editingDescription}
								onChange={e => setEditingDescription(e.target.value)}
								placeholder="Add a description..."
								className="min-h-[120px]"
								autoFocus
							/>
							<div className="flex justify-end gap-2">
								<Button variant="ghost" onClick={() => setEditingDescription(null)}>
									Cancel
								</Button>
								<Button onClick={handleDescriptionBlur}>Save</Button>
							</div>
						</div>
					) : (
						<div
							onClick={() => !isCompleted && setEditingDescription(task.description || '')}
							className={`min-h-[80px] px-4 py-3 text-sm rounded-lg transition-colors ${
								isCompleted
									? 'bg-gray-50 text-gray-400 cursor-default'
									: task.description
										? 'bg-gray-50 text-gray-700 cursor-pointer'
										: 'border border-dashed border-gray-300 text-gray-400 cursor-pointer'
							}`}
						>
							{task.description || 'Add a description...'}
						</div>
					)}
				</div>

				{/* Due Date with quick chips */}
				<div>
					<Label className="block text-xs font-medium mb-1.5 text-gray-500">Due Date</Label>
					<div className="flex items-center gap-2">
						<div className="flex items-center gap-1.5 flex-[2]">
							<button
								type="button"
								onClick={() => !isCompleted && dateInputRef.current?.showPicker()}
								disabled={isCompleted}
								className={`flex-1 flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-left ${
									isCompleted ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 transition-colors'
								}`}
							>
								<svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								<span className={task.dateDue ? 'text-gray-900' : 'text-gray-400'}>
									{formatDate(task.dateDue)}
								</span>
							</button>
							{task.dateDue && !isCompleted && (
								<button
									type="button"
									onClick={() => handleFieldUpdate({ dateDue: null })}
									className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors flex-shrink-0"
									title="Clear date"
								>
									<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							)}
						</div>
						{/* Quick date chips */}
						{!isCompleted && <div className="flex items-center gap-1.5 flex-1">
							{quickDates.map(chip => (
								<button
									key={chip.label}
									type="button"
									onClick={() => handleFieldUpdate({ dateDue: toNoonUTC(chip.value) })}
									className="flex-1 px-1.5 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors text-center whitespace-nowrap"
								>
									{chip.label}
								</button>
							))}
						</div>}
						<input
							ref={dateInputRef}
							type="date"
							value={dueDateStr}
							onChange={e => handleFieldUpdate({ dateDue: e.target.value ? toNoonUTC(e.target.value) : null })}
							className="sr-only"
							tabIndex={-1}
						/>
					</div>
				</div>

				{/* Scheduled time toggle + input. Portfolio tasks too, since MYD-534
				    gave PortfolioTaskCN the same hasScheduledTime contract. */}
				<div className="grid grid-cols-2 gap-4 items-end">
						<label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
							<Switch
								checked={!!task.hasScheduledTime}
								onChange={e => {
									const enabled = e.target.checked
									if (enabled) {
										// Reuse the existing dueDateStr + 09:00 if no time set yet.
										handleFieldUpdate({
											hasScheduledTime: true,
											dateDue: toIsoWithLocalTime(dueDateStr || getToday(), extractTimeHM(task.dateDue)),
										})
									} else {
										handleFieldUpdate({
											hasScheduledTime: false,
											dateDue: dueDateStr ? toNoonUTC(dueDateStr) : null,
										})
									}
								}}
								disabled={isCompleted || !dueDateStr}
							/>
							<span>Set specific time</span>
						</label>
						<div>
							<Label className="block text-xs font-medium mb-1.5 text-gray-500">Time</Label>
							<Input
								type="time"
								value={extractTimeHM(task.dateDue)}
								onChange={e =>
									handleFieldUpdate({
										hasScheduledTime: true,
										dateDue: toIsoWithLocalTime(dueDateStr || getToday(), e.target.value),
									})
								}
								disabled={isCompleted || !task.hasScheduledTime}
							/>
						</div>
					</div>

				{/* Predecessor task */}
				{!isPortfolioTask && (
					<div>
						<Label className="block text-xs font-medium mb-1.5 text-gray-500">
							Predecessor task <span className="text-gray-400">(Optional)</span>
						</Label>
						<Select
							value={
								typeof task.predecessorTaskId === 'object'
									? task.predecessorTaskId?._id || ''
									: task.predecessorTaskId || ''
							}
							onChange={e => handleFieldUpdate({ predecessorTaskId: e.target.value || null })}
							disabled={isCompleted || predecessorOptions.length === 0}
						>
							<option value="">None</option>
							{predecessorOptions.map((t: any) => (
								<option key={t._id} value={t._id}>
									{t.taskName}
								</option>
							))}
						</Select>
					</div>
				)}

				{/* Priority + Effort - 2 columns */}
				<div className="grid grid-cols-2 gap-4">
					<div>
						<Label className="block text-xs font-medium mb-1.5 text-gray-500">Priority</Label>
						<Select
							value={task.taskPriority || ''}
							onChange={e => handleFieldUpdate({ taskPriority: e.target.value })}
							disabled={isCompleted}
						>
							{PRIORITY_OPTIONS.map(opt => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</div>
					<div>
						<Label className="block text-xs font-medium mb-1.5 text-gray-500">Effort</Label>
						<Select
							value={task.taskEffort || ''}
							onChange={e => handleFieldUpdate({ taskEffort: e.target.value })}
							disabled={isCompleted}
						>
							{EFFORT_OPTIONS.map(opt => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</div>
				</div>

				{/* Project (hidden for portfolio-level tasks — they have no project) */}
				{!isPortfolioTask && (
					<div>
						<Label className="block text-xs font-medium mb-1.5 text-gray-500">Project</Label>
						<Select
							value={typeof task.projectid === 'object' ? task.projectid?._id || '' : task.projectid || ''}
							onChange={e => handleFieldUpdate({ projectid: e.target.value || null })}
							disabled={isCompleted}
						>
							<option value="">No Project</option>
							{activeProjects.map((p: any) => (
								<option key={p._id} value={p._id}>
									{formatProjectLabel(p)}
								</option>
							))}
						</Select>
					</div>
				)}

				{/* Assigned To */}
				<div>
					<Label className="block text-xs font-medium mb-1.5 text-gray-500">Assigned To</Label>
					<Select
						value={typeof task.assignedTo === 'object' ? task.assignedTo?._id || '' : task.assignedTo || ''}
						onChange={e => handleFieldUpdate({ assignedTo: e.target.value || null })}
						disabled={isCompleted}
					>
						{assignableUsers.map((u: any) => (
							<option key={u.id} value={u.id}>
								{u.name}{u.id === userInfo?._id ? ' (me)' : ''}
							</option>
						))}
					</Select>
				</div>

				{/* Activity History */}
				<div className="pt-5 border-t border-gray-200">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-2">
							<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<h3 className="text-sm font-semibold text-gray-900">Activity</h3>
							{task.activityHistory?.length > 0 && (
								<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
									{task.activityHistory.length}
								</span>
							)}
						</div>
					</div>
					{!task.activityHistory || task.activityHistory.length === 0 ? (
						<div className="text-center py-6">
							<svg className="w-5 h-5 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<p className="text-sm text-gray-400">No activity yet</p>
						</div>
					) : (
						<div className="flow-root">
							<ul className="-mb-6">
								{task.activityHistory
									.slice()
									.reverse()
									.map((activity: any, idx: number, arr: any[]) => {
										const isLast = idx === arr.length - 1
										const actionConfig = ACTIVITY_ACTION_CONFIG[activity.action] || DEFAULT_ACTION_CONFIG
										const hasFieldChange = activity.field && (activity.oldValue || activity.newValue)
										return (
											<li key={idx} className="relative pb-6 last:pb-0">
												{/* Timeline connector */}
												{!isLast && (
													<span
														className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
														aria-hidden="true"
													/>
												)}
												<div className="relative flex items-start gap-3">
													{/* Parent asset avatar */}
													<Avatar className="h-8 w-8 flex-shrink-0">
														{task.projectid?.projectAsset?.image ? (
															<>
																<AvatarImage src={task.projectid.projectAsset.image} alt={task.projectid.projectAsset.assetName || ''} />
																<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																	{(task.projectid.projectAsset.assetName || '?').charAt(0).toUpperCase()}
																</AvatarFallback>
															</>
														) : (
															<AvatarFallback className="bg-amber-50 text-amber-600">
																<FolderOpen className="w-3.5 h-3.5" />
															</AvatarFallback>
														)}
													</Avatar>
													{/* Content */}
													<div className="min-w-0 flex-1 pt-0.5">
														<div className="flex items-center gap-2">
															<span
																className="text-sm font-medium"
																style={{ color: actionConfig.color }}
															>
																{actionConfig.label}
															</span>
														</div>
														{/* Change details - show for any action with field data */}
														{hasFieldChange && (
															<div className="mt-1 text-sm flex items-center gap-1.5 flex-wrap text-gray-500">
																<span className="font-medium text-gray-700">{formatFieldName(activity.field)}</span>
																{activity.oldValue && activity.oldValue !== 'none' && activity.oldValue !== '' && (
																	<>
																		<span className="line-through opacity-60">{activity.oldValue}</span>
																		<svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
																		</svg>
																	</>
																)}
																{activity.newValue && activity.newValue !== 'none' && (
																	<span className="font-medium text-gray-900">{activity.newValue}</span>
																)}
															</div>
														)}
														{/* Timestamp */}
														<p className="mt-1 text-xs text-gray-400">
															{activity.changedAt && formatActivityDate(activity.changedAt)}
															{activity.changedBy?.name && ` · ${activity.changedBy.name}`}
														</p>
													</div>
												</div>
											</li>
										)
									})}
							</ul>
						</div>
					)}
				</div>
			</div>

			{/* Footer Actions */}
			<div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
				{isCompleted ? (
					<span className="text-xs text-gray-400">Completed tasks cannot be deleted</span>
				) : showDeleteConfirm ? (
					<div className="flex items-center gap-2">
						<span className="text-sm text-red-600">Delete this task?</span>
						<Button variant="danger" onClick={handleDelete}>
							Yes, Delete
						</Button>
						<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
							Cancel
						</Button>
					</div>
				) : (
					<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
						<Trash2 className="w-4 h-4 mr-1" />
						Delete Task
					</Button>
				)}
				<div className="flex items-center gap-3 text-xs text-gray-400">
					<span className="flex items-center gap-1">
						<kbd className="px-1.5 py-0.5 rounded bg-white font-mono text-[10px]">E</kbd>
						edit
					</span>
					<span className="flex items-center gap-1">
						<kbd className="px-1.5 py-0.5 rounded bg-white font-mono text-[10px]">C</kbd>
						complete
					</span>
					<span className="flex items-center gap-1">
						<kbd className="px-1.5 py-0.5 rounded bg-white font-mono text-[10px]">Esc</kbd>
						close
					</span>
				</div>
			</div>
		</Sheet>
	)
}

export default TaskCNDetailSheet
